import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, Fragment, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import s from './Select.module.css';
import { Icon } from '../Icon';
const VIEWPORT_GAP = 8;
const MENU_GAP = 6;
const MAX_MENU_HEIGHT = 288;
function nodeText(node) {
    return Children.toArray(node)
        .map((child) => {
        if (typeof child === 'string' || typeof child === 'number')
            return String(child);
        if (isValidElement(child))
            return nodeText(child.props.children);
        return '';
    })
        .join('')
        .trim();
}
function collectOptions(children, parentDisabled = false) {
    const result = [];
    Children.forEach(children, (child) => {
        if (!isValidElement(child))
            return;
        if (child.type === Fragment) {
            collectOptions(child.props.children, parentDisabled).forEach((option) => result.push(option));
            return;
        }
        if (child.type === 'optgroup') {
            const groupDisabled = parentDisabled || Boolean(child.props.disabled);
            collectOptions(child.props.children, groupDisabled).forEach((option) => result.push(option));
            return;
        }
        if (child.type !== 'option')
            return;
        const option = child;
        const label = option.props.label ?? nodeText(option.props.children);
        result.push({
            value: String(option.props.value ?? label),
            label,
            disabled: parentDisabled || Boolean(option.props.disabled),
        });
    });
    return result;
}
function scalarValue(value) {
    if (value === undefined || value === null || Array.isArray(value))
        return undefined;
    return String(value);
}
function nextEnabledIndex(options, start, direction) {
    if (options.length === 0)
        return -1;
    for (let step = 1; step <= options.length; step += 1) {
        const index = (start + direction * step + options.length) % options.length;
        if (options[index]?.disabled === false)
            return index;
    }
    return -1;
}
function associatedLabelText(control) {
    const label = control?.labels?.[0];
    if (!label)
        return undefined;
    const clone = label.cloneNode(true);
    clone.querySelectorAll('button, select, input, textarea').forEach((element) => element.remove());
    return clone.textContent?.trim() || undefined;
}
export function Select({ className, children, value, defaultValue, onChange, disabled, id, name, required, form, autoFocus, title, style, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, ...nativeProps }) {
    const options = useMemo(() => collectOptions(children), [children]);
    const generatedId = useId().replace(/:/g, '');
    const triggerId = id ?? `select-${generatedId}`;
    const listboxId = `${triggerId}-listbox`;
    const nativeId = `${triggerId}-native`;
    const controlledValue = scalarValue(value);
    const initialValue = scalarValue(defaultValue) ?? options.find((option) => !option.disabled)?.value ?? '';
    const [internalValue, setInternalValue] = useState(initialValue);
    const selectedValue = controlledValue ?? internalValue;
    const selectedIndex = options.findIndex((option) => option.value === selectedValue);
    const selectedOption = options[selectedIndex] ?? options[0];
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(selectedIndex);
    const [position, setPosition] = useState(null);
    const [menuLabel, setMenuLabel] = useState(ariaLabel);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const nativeRef = useRef(null);
    useEffect(() => {
        if (controlledValue === undefined &&
            !options.some((option) => option.value === internalValue)) {
            setInternalValue(options.find((option) => !option.disabled)?.value ?? '');
        }
    }, [controlledValue, internalValue, options]);
    useEffect(() => {
        if (disabled)
            setOpen(false);
    }, [disabled]);
    useEffect(() => {
        if (controlledValue !== undefined)
            return;
        const nativeSelect = nativeRef.current;
        const ownerForm = nativeSelect?.form;
        if (!nativeSelect || !ownerForm)
            return;
        let resetTimer;
        const syncAfterReset = () => {
            window.clearTimeout(resetTimer);
            resetTimer = window.setTimeout(() => {
                setInternalValue(nativeSelect.value);
                setOpen(false);
            });
        };
        ownerForm.addEventListener('reset', syncAfterReset);
        return () => {
            ownerForm.removeEventListener('reset', syncAfterReset);
            window.clearTimeout(resetTimer);
        };
    }, [controlledValue]);
    useEffect(() => {
        if (!open)
            return;
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : nextEnabledIndex(options, -1, 1));
        setMenuLabel(ariaLabel ?? associatedLabelText(triggerRef.current));
    }, [ariaLabel, open, options, selectedIndex]);
    useLayoutEffect(() => {
        if (!open)
            return;
        const updatePosition = () => {
            const trigger = triggerRef.current;
            if (!trigger)
                return;
            const rect = trigger.getBoundingClientRect();
            const availableBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_GAP;
            const availableAbove = rect.top - MENU_GAP - VIEWPORT_GAP;
            const estimatedHeight = Math.min(MAX_MENU_HEIGHT, options.length * 38 + 10);
            const placement = availableBelow < Math.min(estimatedHeight, 160) && availableAbove > availableBelow
                ? 'above'
                : 'below';
            const availableHeight = placement === 'above' ? availableAbove : availableBelow;
            const width = Math.min(Math.max(rect.width, 176), Math.max(0, window.innerWidth - VIEWPORT_GAP * 2));
            const left = Math.min(Math.max(VIEWPORT_GAP, rect.left), Math.max(VIEWPORT_GAP, window.innerWidth - width - VIEWPORT_GAP));
            setPosition({
                left,
                top: placement === 'above' ? rect.top - MENU_GAP : rect.bottom + MENU_GAP,
                width,
                maxHeight: Math.max(88, Math.min(MAX_MENU_HEIGHT, availableHeight)),
                placement,
            });
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, options.length]);
    useEffect(() => {
        if (!open)
            return;
        const closeOutside = (event) => {
            const target = event.target;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target))
                return;
            setOpen(false);
        };
        document.addEventListener('mousedown', closeOutside);
        return () => document.removeEventListener('mousedown', closeOutside);
    }, [open]);
    useEffect(() => {
        if (!open || activeIndex < 0)
            return;
        const activeOption = menuRef.current?.querySelector(`[data-option-index="${activeIndex}"]`);
        activeOption?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex, open]);
    const openMenu = () => {
        if (disabled || options.length === 0)
            return;
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : nextEnabledIndex(options, -1, 1));
        setOpen(true);
    };
    const selectOption = (option) => {
        if (disabled || option.disabled)
            return;
        if (option.value === selectedValue) {
            setOpen(false);
            triggerRef.current?.focus();
            return;
        }
        const nativeSelect = nativeRef.current;
        if (!nativeSelect)
            return;
        const valueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
        valueSetter?.call(nativeSelect, option.value);
        if (controlledValue === undefined)
            setInternalValue(option.value);
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        setOpen(false);
        triggerRef.current?.focus();
    };
    const handleKeyDown = (event) => {
        if (disabled)
            return;
        if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            return;
        }
        if (event.key === 'Tab') {
            setOpen(false);
            return;
        }
        if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            if (!open)
                openMenu();
            const edge = event.key === 'Home' ? -1 : 0;
            const direction = event.key === 'Home' ? 1 : -1;
            setActiveIndex(nextEnabledIndex(options, edge, direction));
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) {
                openMenu();
                return;
            }
            setActiveIndex((current) => nextEnabledIndex(options, current, event.key === 'ArrowDown' ? 1 : -1));
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!open) {
                openMenu();
                return;
            }
            const option = options[activeIndex];
            if (option)
                selectOption(option);
        }
    };
    return (_jsxs("span", { className: [s.wrap, className].filter(Boolean).join(' '), children: [_jsxs("button", { ref: triggerRef, id: triggerId, type: "button", className: s.trigger, role: "combobox", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-required": required || undefined, "aria-haspopup": "listbox", "aria-controls": open ? listboxId : undefined, "aria-expanded": open, "aria-activedescendant": open && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined, disabled: disabled, autoFocus: autoFocus, title: title, style: style, onClick: () => (open ? setOpen(false) : openMenu()), onKeyDown: handleKeyDown, children: [_jsx("span", { className: s.value, children: selectedOption?.label || '—' }), _jsx(Icon, { name: "chev-down", size: 14, className: s.chev })] }), _jsx("select", { ...nativeProps, ref: nativeRef, id: nativeId, className: s.native, name: name, form: form, required: required, disabled: disabled, value: value, defaultValue: defaultValue, onChange: (event) => {
                    if (controlledValue === undefined)
                        setInternalValue(event.currentTarget.value);
                    onChange?.(event);
                }, "aria-hidden": "true", tabIndex: -1, children: children }), open &&
                position &&
                createPortal(_jsx("div", { ref: menuRef, id: listboxId, role: "listbox", "aria-label": menuLabel, "aria-labelledby": ariaLabelledBy, className: s.menu, "data-placement": position.placement, style: {
                        left: position.left,
                        top: position.top,
                        width: position.width,
                        maxHeight: position.maxHeight,
                    }, children: options.map((option, index) => {
                        const selected = option.value === selectedValue;
                        const active = index === activeIndex;
                        return (_jsxs("button", { id: `${listboxId}-${index}`, type: "button", role: "option", "aria-selected": selected, "aria-disabled": option.disabled || undefined, disabled: disabled || option.disabled, className: s.option, "data-active": active || undefined, "data-selected": selected || undefined, "data-option-index": index, onMouseEnter: () => !option.disabled && setActiveIndex(index), onClick: () => selectOption(option), children: [_jsx("span", { className: s.optionLabel, children: option.label }), _jsx("span", { className: s.check, "aria-hidden": "true", children: selected && _jsx(Icon, { name: "check", size: 14 }) })] }, `${option.value}-${index}`));
                    }) }), document.body)] }));
}
